import { zodResolver } from "@hookform/resolvers/zod";
import { errMsgFocusStyle, inputStyle, LINKED_IN_URL, verifyPhoneNumber, WORK_EMAIL_REGEX } from "@utils/form";
import { insertDemoDataInDB } from "database/supabase/postFormData";
import { getAffiliateId } from "@utils/campaign-tracker";
import React, { ComponentType, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Controller, useForm } from "react-hook-form";
import IntlTelInput from "react-intl-tel-input";
import "react-intl-tel-input/dist/main.css";
import Modal from "react-modal";
import ReactPlayer from "react-player";
import { z } from "zod";
import { FormErrorMessage } from "./WAChatbutton/WAform/WidgetForm";
import { ClipLoader } from "react-spinners";
import { IoCloseOutline } from "react-icons/io5";

const ReactModal = Modal as unknown as ComponentType<ReactModal.Props>;

interface DemoForm {
    companyName: string;
    countryCode: string;
    phoneNumber: string;
    emailId: string;
    linkedInURL: string;
}

const WatchDemoFormSchema = z.object({
    companyName: z.string({ required_error: "Company Name is required" }).min(3, "Company Name must be at least 3 characters").max(256, "Maximum limit exceeded"),
    countryCode: z.string(),
    phoneNumber: z.string({ required_error: "Phone number is required" }).refine((phoneNumber) => verifyPhoneNumber(phoneNumber)),
    emailId: z.string({ required_error: "Mail ID is required" }).email().regex(WORK_EMAIL_REGEX),
    linkedInURL: z
        .string({ required_error: "LinkedIn Url is required" })
        .url()
        .min(3, "LinkedIn Url must be at least 3 characters")
        .max(1024, "Maximum limit exceeded")
        .regex(LINKED_IN_URL),
});

const siteKey = process.env.NEXT_PUBLIC_API_RECAPTCHA_SITE_KEY;

const BookDemoForm: React.FC = () => {
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        getValues,
        reset,
    } = useForm<DemoForm>({
        resolver: zodResolver(WatchDemoFormSchema),
    });

    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const handleRecaptcha = (token: string | null) => {
        if (!token) return;
        recaptchaRef.current?.reset();
        onSubmit(token);
    };

    const triggerCaptcha = () => {
        setIsLoading(true);
        recaptchaRef.current?.execute();
    };

    const onSubmit = async (token: string) => {
        setIsLoading(true);
        const formData = getValues();
        const formattedData = {
            company_name: formData.companyName,
            phone_number: formData.countryCode + formData.phoneNumber,
            email_id: formData.emailId,
            linkedin_url: formData.linkedInURL,
            source: "watch-demo",
            recaptchaToken: token,
            affiliateId: getAffiliateId(),
        };
        try {
            await insertDemoDataInDB(formattedData);
            setIsLoading(false);
            setErrorMessage(false);
            setShowModal(true);
            reset();
        } catch (err) {
            console.error("error", err);
            setErrorMessage(true);
            setIsLoading(false);
        }
    };

    return (
        <section className="lg:max-w-6xl lg:min-w-6xl lg:mx-auto w-fit mx-3 lg:px-20 py-10 px-5 bg-white border border-solid border-gray-200 rounded-xl shadow-xl">
            <form className="flex flex-col gap-10" onSubmit={handleSubmit(triggerCaptcha)}>
                <h3 className="text-3xl font-bold text-gray-700 text-center">Tell us a little about yourself 👋</h3>
                <div className="flex flex-col gap-8">
                    <div className="flex flex-col gap-2">
                        <DemoFormLabel>Name</DemoFormLabel>
                        <input
                            style={{ ...inputStyle, ...(errors.companyName?.message ? errMsgFocusStyle : {}) }}
                            id="companyName"
                            type="text"
                            placeholder="Name"
                            className="text-base font-normal text-gray-400 h-10"
                            {...register("companyName")}
                        />
                        {errors.companyName && <FormErrorMessage>{errors.companyName.message}</FormErrorMessage>}
                    </div>
                    <div className="flex flex-col gap-2 z-0">
                        <DemoFormLabel>WhatsApp Number</DemoFormLabel>
                        <Controller
                            name="phoneNumber"
                            control={control}
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                                <IntlTelInput
                                    containerClassName="intl-tel-input w-full h-10"
                                    style={{ backgroundColor: "#F7FAFC" }}
                                    inputClassName="form-control w-full h-10"
                                    placeholder="Enter WhatsApp Number"
                                    defaultCountry="in"
                                    value={value}
                                    onSelectFlag={(_, selectedCountryData) => {
                                        if (selectedCountryData.dialCode) {
                                            setValue("countryCode", selectedCountryData.dialCode);
                                        }
                                    }}
                                    onPhoneNumberChange={(_, value, selectedCountryData) => {
                                        if (selectedCountryData.dialCode) {
                                            setValue("countryCode", selectedCountryData.dialCode);
                                        }
                                        onChange(value);
                                    }}
                                />
                            )}
                        />
                        {!errors.phoneNumber?.message ? (
                            <span className="text-base font-normal text-gray-500">Note: Check if the country code have entered is correct</span>
                        ) : (
                            <FormErrorMessage>{errors.phoneNumber.message}</FormErrorMessage>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <DemoFormLabel>Work Email</DemoFormLabel>
                        <p className="text-base font-normal text-gray-500">To book a 1-on-1 demo, please use your business email</p>
                        <input
                            style={{ ...inputStyle, ...(errors.emailId?.message ? errMsgFocusStyle : {}) }}
                            id="emailId"
                            type="email"
                            placeholder="Your E-Mail ID"
                            className="text-base font-normal text-gray-400 h-10"
                            {...register("emailId")}
                        />
                        {errors.emailId && <FormErrorMessage>{errors.emailId.message}</FormErrorMessage>}
                    </div>
                    <div className="flex flex-col gap-2">
                        <DemoFormLabel>Company LinkedIn URL</DemoFormLabel>
                        <input
                            style={{ ...inputStyle, ...(errors.linkedInURL?.message ? errMsgFocusStyle : {}) }}
                            id="linkedInURL"
                            type="text"
                            placeholder="Add your Company URL"
                            className="text-base font-normal text-gray-400 h-10"
                            {...register("linkedInURL")}
                        />
                        {errors.linkedInURL && <FormErrorMessage>{errors.linkedInURL.message}</FormErrorMessage>}
                    </div>
                </div>
                <div className="flex flex-col  items-center gap-3">
                    <button className="focus:outline-none bg-blue-500 rounded-md px-6 py-2.5 text-lg text-white font-semibold w-fit hover:bg-blue-600 hover:shadow-md">
                        {isLoading ? (
                            <div className="flex gap-3 items-center justify-center">
                                <ClipLoader color={"#ffffff"} loading={true} size={20} />
                            </div>
                        ) : (
                            "View Demo"
                        )}
                    </button>
                    {errorMessage && <p className="text-base text-red-500 font-medium">* Something went wrong</p>}
                </div>
            </form>
            {/* @ts-expect-error react-google-recaptcha ref typing does not match the forwarded ref instance */}
            {siteKey && <ReCAPTCHA ref={recaptchaRef} sitekey={siteKey} size="invisible" onChange={handleRecaptcha} />}
            <ReactModal
                isOpen={showModal}
                onRequestClose={() => setShowModal(false)}
                contentLabel="Demo Video"
                className="modal flex flex-col gap-4 items-end"
                overlayClassName="overlay"
            >
                <IoCloseOutline size="24px" className="text-gray-500 rounded-full hover:shadow-lg hover:text-white hover:bg-gray-400" onClick={() => setShowModal(false)} />
                <div className="video-container">
                    <ReactPlayer url="https://www.youtube.com/watch?v=ChhbpssjzhY" controls={true} width="100%" height="100%" />
                </div>
                <a
                    href="https://app.gallabox.com/signup"
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-2 font-semibold text-sm rounded-lg bg-blue-500 text-white hover:bg-blue-600 shadow-lg hover:no-underline w-fit"
                >
                    Start Free Trial
                </a>
            </ReactModal>
        </section>
    );
};

export default BookDemoForm;

type DemoFormLabelProps = React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>;
const DemoFormLabel: React.FC<DemoFormLabelProps> = (props) => {
    const { children, ...labelProps } = props;
    return (
        <label {...labelProps} className="text-2xl font-medium text-gray-600">
            {children}
        </label>
    );
};
